/**
 * Cameras Hooks
 *
 * TanStack Query hooks for camera configuration CRUD operations.
 * Used for managing Alta Avigilon camera configurations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import { stationKeys } from './useStations';

// Types for camera data
export interface CameraConfiguration {
  id: string;
  orgId: string;
  cameraId: string;
  name: string;
  location: string | null;
  departmentId: string | null;
  stationId: string | null; // Phase 9.2
  defaultSopId: string | null;
  captureEnabled: boolean;
  captureIntervalMinutes: number;
  captureDurationMinutes: number;
  sourceCount: number; // Phase 34: 1-based lens count (multi-sensor cameras record one stream per lens)
  // Phase 11: Removed captureStartHour/captureEndHour - now 24/7 operation
  altaDeploymentName: string | null;
  isActive: boolean;
  lastCaptureAt: string | null;
  lastCaptureStatus: string | null;
  createdAt: string;
  updatedAt: string;
  department?: { id: string; name: string } | null;
  station?: { id: string; name: string } | null; // Phase 9.2
  defaultSop?: { id: string; title: string } | null;
  sops?: { id: string; title: string }[]; // Multi-SOP assignment
  videoClipsCount?: number;
  linkedSopCount?: number;
}

export interface CameraLookup {
  id: string;
  cameraId: string;
  name: string;
  location: string | null;
  sourceCount: number; // Phase 34: feeds the template lens dropdown
}

export interface CreateCamera {
  cameraId: string;
  name: string;
  location?: string;
  departmentId?: string | null;
  stationId?: string | null; // Phase 9.2
  defaultSopId?: string | null;
  sopIds?: string[]; // Multi-SOP assignment
  captureEnabled?: boolean;
  captureIntervalMinutes?: number;
  captureDurationMinutes?: number;
  // Phase 34 (F1): 1-8. Omit on create to let the server default from the
  // synced Alta registry (D6); explicit values always win.
  sourceCount?: number;
  // Phase 11: Removed captureStartHour/captureEndHour - now 24/7 operation
  altaDeploymentName?: string;
}

export interface UpdateCamera extends Partial<CreateCamera> {}

export interface CameraTestResult {
  cameraId: string;
  name: string;
  isOnline: boolean;
  lastChecked: string;
  error?: string;
}

export interface AltaConnectionResult {
  deployment: string;
  region: string;
  connected: boolean;
  error?: string;
}

// Query keys
export const cameraKeys = {
  all: ['cameras'] as const,
  lists: () => [...cameraKeys.all, 'list'] as const,
  list: (filters: { includeInactive?: boolean; departmentId?: string }) =>
    [...cameraKeys.lists(), filters] as const,
  lookup: () => [...cameraKeys.all, 'lookup'] as const,
  details: () => [...cameraKeys.all, 'detail'] as const,
  detail: (id: string) => [...cameraKeys.details(), id] as const,
};

/**
 * Fetch cameras list with department and SOP info
 */
export function useCameras(options?: { includeInactive?: boolean; departmentId?: string }) {
  const api = useApi();

  return useQuery({
    queryKey: cameraKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.includeInactive) params.set('includeInactive', 'true');
      if (options?.departmentId) params.set('departmentId', options.departmentId);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/cameras?${queryString}` : '/api/cameras';

      return api.get<CameraConfiguration[]>(endpoint);
    },
  });
}

/**
 * Fetch lightweight cameras list for dropdowns
 */
export function useCameraLookup() {
  const api = useApi();

  return useQuery({
    queryKey: cameraKeys.lookup(),
    queryFn: () => api.get<CameraLookup[]>('/api/cameras/lookup'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch single camera details
 */
export function useCamera(id: string) {
  const api = useApi();

  return useQuery({
    queryKey: cameraKeys.detail(id),
    queryFn: () => api.get<CameraConfiguration>(`/api/cameras/${id}`),
    enabled: !!id,
  });
}

/**
 * Create camera mutation
 */
export function useCreateCamera() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCamera) =>
      api.post<CameraConfiguration>('/api/cameras', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cameraKeys.all });
      // Phase 9.2: Invalidate station counts
      queryClient.invalidateQueries({ queryKey: stationKeys.lists() });
    },
  });
}

/**
 * Update camera mutation
 */
export function useUpdateCamera() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCamera }) =>
      api.put<CameraConfiguration>(`/api/cameras/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: cameraKeys.lists() });
      queryClient.invalidateQueries({ queryKey: cameraKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: cameraKeys.lookup() });
      // Phase 9.2: Invalidate station counts
      queryClient.invalidateQueries({ queryKey: stationKeys.lists() });
    },
  });
}

/**
 * Delete camera mutation (soft delete)
 */
export function useDeleteCamera() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/api/cameras/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cameraKeys.all });
      // Phase 9.2: Invalidate station counts
      queryClient.invalidateQueries({ queryKey: stationKeys.lists() });
    },
  });
}

/**
 * Test camera connection mutation
 */
export function useTestCamera() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<CameraTestResult>(`/api/cameras/${id}/test`, {}),
    onSuccess: (_, id) => {
      // Refresh camera data to get updated status
      queryClient.invalidateQueries({ queryKey: cameraKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: cameraKeys.lists() });
    },
  });
}

/**
 * Test Alta API connection mutation (admin only)
 */
export function useTestAltaConnection() {
  const api = useApi();

  return useMutation({
    mutationFn: () =>
      api.post<AltaConnectionResult>('/api/cameras/test-connection', {}),
  });
}

// ============================================
// AVAILABLE CAMERAS (for camera dropdown)
// ============================================

/**
 * Available camera from Alta registry
 */
export interface AvailableCamera {
  id: string;
  altaDeviceId: string;
  altaDeviceName: string;
  altaLocation: string | null;
  altaModel: string | null;
  status: string;
  // Included for super_admins to know which org the camera is for
  assignedOrgId?: string | null;
  assignedOrgName?: string | null;
}

/**
 * Fetch available cameras (assigned to org, not yet configured)
 * Used by admins when adding new camera configurations
 */
export function useAvailableCameras() {
  const api = useApi();

  return useQuery({
    queryKey: [...cameraKeys.all, 'available'] as const,
    queryFn: () => api.get<AvailableCamera[]>('/api/cameras/available'),
    staleTime: 30 * 1000, // 30 second cache
  });
}

// ============================================
// SUPER ADMIN - Alta Camera Registry
// ============================================

/**
 * Alta camera in registry
 */
export interface AltaCamera {
  id: string;
  altaDeviceId: string;
  altaDeviceName: string;
  altaLocation: string | null;
  altaModel: string | null;
  status: string;
  assignedOrgId: string | null;
  assignedBy: string | null;
  assignedAt: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
  assignedOrg?: { id: string; name: string } | null;
  assignedByUser?: { id: string; fullName: string } | null;
}

/**
 * Sync result from Alta
 */
export interface SyncResult {
  added: number;
  updated: number;
  unchanged: number;
  total: number;
  errors: string[];
  syncedAt: string;
}

/**
 * Sync status
 */
export interface SyncStatus {
  totalCameras: number;
  assignedCameras: number;
  unassignedCameras: number;
  lastSyncedAt: string | null;
}

/**
 * Organization lookup for assignment dropdown
 */
export interface OrganizationLookup {
  id: string;
  name: string;
}

// Query keys for super admin
export const altaKeys = {
  all: ['alta'] as const,
  cameras: () => [...altaKeys.all, 'cameras'] as const,
  status: () => [...altaKeys.all, 'status'] as const,
  organizations: () => [...altaKeys.all, 'organizations'] as const,
};

/**
 * Fetch all cameras from Alta registry (super admin only)
 */
export function useAllAltaCameras() {
  const api = useApi();

  return useQuery({
    queryKey: altaKeys.cameras(),
    queryFn: () => api.get<AltaCamera[]>('/api/cameras/alta/all'),
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch sync status (super admin only)
 */
export function useSyncStatus() {
  const api = useApi();

  return useQuery({
    queryKey: altaKeys.status(),
    queryFn: () => api.get<SyncStatus>('/api/cameras/alta/status'),
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch organizations for assignment dropdown (super admin only)
 */
export function useAltaOrganizations() {
  const api = useApi();

  return useQuery({
    queryKey: altaKeys.organizations(),
    queryFn: () => api.get<OrganizationLookup[]>('/api/cameras/alta/organizations'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Sync cameras from Alta API (super admin only)
 */
export function useSyncAltaCameras() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<SyncResult>('/api/cameras/alta/sync', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: altaKeys.cameras() });
      queryClient.invalidateQueries({ queryKey: altaKeys.status() });
      queryClient.invalidateQueries({ queryKey: [...cameraKeys.all, 'available'] });
    },
  });
}

/**
 * Assign camera to organization (super admin only)
 */
export function useAssignCamera() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ altaCameraId, orgId }: { altaCameraId: string; orgId: string }) =>
      api.post<AltaCamera>('/api/cameras/alta/assign', { altaCameraId, orgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: altaKeys.cameras() });
      queryClient.invalidateQueries({ queryKey: altaKeys.status() });
      queryClient.invalidateQueries({ queryKey: [...cameraKeys.all, 'available'] });
    },
  });
}

/**
 * Unassign camera from organization (super admin only)
 */
export function useUnassignCamera() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ message: string; data: AltaCamera }>(`/api/cameras/alta/assign/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: altaKeys.cameras() });
      queryClient.invalidateQueries({ queryKey: altaKeys.status() });
      queryClient.invalidateQueries({ queryKey: [...cameraKeys.all, 'available'] });
    },
  });
}
